import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { useAppLocation } from '../contexts/LocationContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, PartyPopper, Sparkles, Music, Camera, Utensils, Building2,
  Palette, MapPin, Check, ChevronDown, Plus, Star, Send, Calendar,
  CheckCircle, X, Locate
} from 'lucide-react';
import { LANDING_EVENT_TYPES as PRIMARY_EVENT_TYPES } from '../constants/primaryEventTypes';
import { CeleyLoader } from '../components/ui/CeleyLoader';
import FiveTapToDiagnostics from '../components/FiveTapToDiagnostics';
import Footer from '../components/Footer';
import AppStorePromo from '../components/AppStorePromo';
import { getConciergeAvailability } from '../services/conciergeAvailability';

const POPULAR_SERVICES = [
  { value: 'DJ', label: 'DJ', icon: Music },
  { value: 'Photographer', label: 'Photographer', icon: Camera },
  { value: 'Videographer', label: 'Videographer', icon: Camera },
  { value: 'Caterer', label: 'Catering', icon: Utensils },
  { value: 'Venue', label: 'Venue', icon: Building2 },
  { value: 'Balloon Artist', label: 'Balloons', icon: PartyPopper },
  { value: 'Event Decor & Styling', label: 'Decor', icon: Palette },
  { value: 'Photo Booth Provider', label: 'Photo Booth', icon: Camera },
];

export default function CustomerLandingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const appLoc = useAppLocation();

  // Redirect logged-in users
  useEffect(() => {
    if (user) {
      if (user.role === 'vendor') navigate('/vendor/dashboard', { replace: true });
      else if (user.role === 'admin') navigate('/admin/dashboard', { replace: true });
      else navigate('/customer-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const [eventType, setEventType] = useState('Birthday Party');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showServicesPicker, setShowServicesPicker] = useState(false);
  const [locationZip, setLocationZip] = useState('');
  // Phase 4 — Concierge fork CTA visibility. Defaults to hidden so legacy
  // behavior is preserved when the backend availability endpoint hasn't
  // resolved yet (or when the flag is off).
  const [conciergeEnabled, setConciergeEnabled] = useState(false);
  const plannerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    getConciergeAvailability().then((res) => {
      if (!cancelled) setConciergeEnabled(!!res?.enabled);
    });
    return () => { cancelled = true; };
  }, []);
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');

  // Prefill location from LocationContext
  useEffect(() => {
    if (appLoc.hasLocation) {
      setLocationCity(appLoc.location.city || '');
      setLocationState(appLoc.location.state || '');
      setLocationZip(appLoc.location.zip || '');
    }
  }, [appLoc.hasLocation, appLoc.location]);

  const lookupZip = useCallback(async (zip) => {
    if (!/^\d{5}$/.test(zip)) return;
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          setLocationCity(place['place name']);
          setLocationState(place['state abbreviation']);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const toggleService = (value) => {
    setSelectedServices(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const locationDisplay = locationCity
    ? `${locationCity}, ${locationState}`
    : '';
  const locationResolved = !!locationCity;

  const handleSeeMatches = () => {
    const hasLocation = !!(locationCity || locationZip);
    const targetStep = (eventType && selectedServices.length > 0 && hasLocation) ? 4 : 3;

    localStorage.setItem('eventDraft', JSON.stringify({
      eventData: {
        event_type: eventType || '',
        event_date: new Date().toISOString().split('T')[0],
        event_time: '',
        budget: '',
        guest_count: '',
        location_zip: locationZip,
        location_city: locationCity,
        location_state: locationState,
        location_lat: null,
        location_lng: null,
        selected_services: selectedServices,
        selected_vendors: [],
        customer_message: ''
      },
      currentStep: targetStep,
      draftVersion: 3
    }));
    navigate('/plan-event');
  };

  const handleDetectLocation = async () => {
    const result = await appLoc.detectLocation();
    if (result) {
      setLocationCity(result.city || '');
      setLocationState(result.state || '');
      setLocationZip(result.zip || '');
    }
  };

  // Feb 2026: native-cold-start race fix. While auth is still bootstrapping,
  // show the neutral loader instead of the logged-out landing hero. Without
  // this gate, native iOS WKWebView users (whose refresh flow can take 2-8s)
  // briefly see the landing page → tap Login → start typing → get jolted
  // into the dashboard mid-typing when checkAuth() finally resolves.
  if (authLoading) return <CeleyLoader testid="landing-bootstrap-loader" />;

  if (user) return null;

  const selectedType = PRIMARY_EVENT_TYPES.find(t => t.value === eventType);
  const servicesLabel = selectedServices.length > 0
    ? selectedServices.map(s => POPULAR_SERVICES.find(p => p.value === s)?.label || s).join(' · ')
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/30">
      {/* Minimal header */}
      <header className="px-6 pt-6 pb-2 flex items-center justify-between max-w-lg mx-auto">
        <FiveTapToDiagnostics
          className="flex items-center gap-1.5 cursor-pointer"
          data-testid="celey-wordmark-5tap"
        >
          <div className="flex items-center gap-1.5" onClick={() => navigate('/')}>
            <img src="/celeyapp-icon-green.png" alt="Celey" className="h-6 w-6" />
            <span className="text-xl font-bold text-gray-900 tracking-tight">Celey</span>
          </div>
        </FiveTapToDiagnostics>
        <button
          onClick={() => navigate('/auth?mode=login')}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
          data-testid="landing-login-btn"
        >
          Log In
        </button>
      </header>

      <main className="max-w-lg mx-auto px-6 pb-20">
        {/* Hero */}
        <section className="pt-12 pb-10 text-center" data-testid="landing-hero">
          <h1 className="text-4xl font-bold text-gray-900 leading-tight tracking-tight">
            Plan your event.<br />
            Find your vendors.<br />
            All in one place.
          </h1>
          <p className="mt-4 text-gray-500 text-base">
            Start with your event — we'll show you who can help.
          </p>
        </section>

        {/* Two ways to plan — orientation fork (V1.1). Concierge card is
            gated by the same runtime availability flag as the bottom fork;
            when Concierge is off this whole strip disappears and the page
            reads exactly like the legacy Guided-Planner-only experience. */}
        {conciergeEnabled && (
          <section className="pb-8" data-testid="landing-two-ways">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 text-center mb-3">
              Two ways to plan
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => plannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 hover:border-emerald-200 transition-colors"
                data-testid="two-ways-guided-card"
              >
                <PartyPopper className="w-5 h-5 text-emerald-600" />
                <p className="mt-2 font-semibold text-gray-900 text-sm">Guided Planner</p>
                <p className="mt-1 text-xs text-gray-500 leading-snug">
                  Plan it yourself with Celey's tools and marketplace.
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  Start below <ArrowRight className="w-3 h-3" />
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/concierge/intake')}
                className="text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-4 hover:border-emerald-200 transition-colors"
                data-testid="two-ways-concierge-card"
              >
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <p className="mt-2 font-semibold text-gray-900 text-sm">Concierge</p>
                <p className="mt-1 text-xs text-gray-500 leading-snug">
                  Tell us what you're planning. Celey will help find and recommend options.
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  Let Celey Help <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            </div>
          </section>
        )}

        {/* Input controls */}
        <section ref={plannerRef} className="space-y-3 pb-6 scroll-mt-4" data-testid="landing-inputs">
          {/* Event Type Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-left hover:border-emerald-300 transition-colors"
              data-testid="landing-event-type-btn"
            >
              <div className="flex items-center gap-2.5">
                {selectedType
                  ? <selectedType.icon className="w-5 h-5 text-emerald-600" />
                  : <PartyPopper className="w-5 h-5 text-gray-300" />
                }
                <span className={selectedType ? 'font-medium text-gray-900' : 'text-gray-400'}>
                  {selectedType ? selectedType.label : 'Event type'}
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showTypeDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 animate-in fade-in slide-in-from-top-2 duration-150" data-testid="landing-event-type-dropdown">
                {PRIMARY_EVENT_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => { setEventType(type.value); setShowTypeDropdown(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left"
                      data-testid={`landing-type-${type.value.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-gray-800">{type.label}</span>
                      {eventType === type.value && <Check className="w-4 h-4 text-emerald-600 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Services + Location row */}
          <div className="flex gap-2">
            {/* Services selector */}
            <button
              onClick={() => setShowServicesPicker(!showServicesPicker)}
              className="flex-1 flex items-center gap-2 px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-left hover:border-emerald-300 transition-colors min-w-0"
              data-testid="landing-services-btn"
            >
              <Music className="w-4 h-4 text-gray-300 shrink-0" />
              <span className={`truncate text-sm ${selectedServices.length > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                {servicesLabel || 'Services'}
              </span>
            </button>

            {/* Location */}
            <div className="relative">
              {locationResolved ? (
                <button
                  onClick={() => { setLocationCity(''); setLocationState(''); setLocationZip(''); }}
                  className="flex items-center gap-1.5 px-4 py-3.5 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 transition-colors whitespace-nowrap"
                  data-testid="landing-location-display"
                >
                  <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-sm font-medium text-gray-900">{locationDisplay}</span>
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    placeholder="ZIP"
                    value={locationZip}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setLocationZip(val);
                      if (/^\d{5}$/.test(val)) lookupZip(val);
                    }}
                    className="w-20 px-3 py-3.5 bg-white border border-gray-200 rounded-xl text-sm text-center placeholder:text-gray-400 hover:border-emerald-300 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none transition-colors"
                    data-testid="landing-zip-input"
                  />
                  {appLoc.geoStatus !== 'detecting' && (
                    <button
                      onClick={handleDetectLocation}
                      className="p-3.5 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 transition-colors"
                      title="Detect my location"
                      data-testid="landing-detect-btn"
                    >
                      <Locate className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  {appLoc.geoStatus === 'detecting' && (
                    <div className="p-3.5">
                      <Locate className="w-4 h-4 text-gray-400 animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Services picker sheet */}
          {showServicesPicker && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-lg animate-in fade-in slide-in-from-top-2 duration-150" data-testid="landing-services-picker">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">Select services</p>
                <button onClick={() => setShowServicesPicker(false)} className="text-xs text-emerald-600 font-medium">Done</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {POPULAR_SERVICES.map(svc => {
                  const Icon = svc.icon;
                  const isSelected = selectedServices.includes(svc.value);
                  return (
                    <button
                      key={svc.value}
                      onClick={() => toggleService(svc.value)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`landing-svc-${svc.value.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="w-3 h-3" />
                      {svc.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Primary CTA */}
          <Button
            onClick={handleSeeMatches}
            className="w-full py-6 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-base font-semibold shadow-sm"
            data-testid="landing-see-matches-btn"
          >
            See your matches <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-100 my-10" />

        {/* How It Works — static preview cards */}
        <section className="pb-10" data-testid="landing-how-it-works">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-6">How It Works</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2 snap-x snap-mandatory scrollbar-hide">
            {/* Card 1: Select vendors */}
            <div className="min-w-[260px] max-w-[280px] snap-start bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden shrink-0">
              <div className="h-28 bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
                <div className="flex -space-x-3">
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center"><Music className="w-6 h-6 text-purple-500" /></div>
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center"><Camera className="w-6 h-6 text-blue-500" /></div>
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center"><Utensils className="w-6 h-6 text-orange-500" /></div>
                </div>
              </div>
              <div className="p-4">
                <p className="font-semibold text-gray-900 text-sm">Select Vendors</p>
                <p className="text-xs text-gray-400 mt-0.5">Browse and add to your plan</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Mike's DJ Services</span>
                    <span className="text-emerald-600 font-medium">$300</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Bella's Catering</span>
                    <span className="text-emerald-600 font-medium">$1,000</span>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-medium">
                    <Plus className="w-3 h-3" /> Add
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Selected vendors */}
            <div className="min-w-[260px] max-w-[280px] snap-start bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden shrink-0">
              <div className="h-28 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
                  <p className="text-xs font-medium text-gray-600 mt-1">2 selected</p>
                </div>
              </div>
              <div className="p-4">
                <p className="font-semibold text-gray-900 text-sm">Your Selections</p>
                <p className="text-xs text-gray-400 mt-0.5">Review before sending</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
                    <span className="text-gray-700">DJ</span>
                    <span className="text-gray-400">Quote on request</span>
                  </div>
                  <div className="flex items-center justify-between text-xs py-1.5">
                    <span className="text-gray-700">Catering</span>
                    <span className="text-emerald-600 font-medium">Starting at $1,000</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Review & Send */}
            <div className="min-w-[260px] max-w-[280px] snap-start bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden shrink-0">
              <div className="h-28 bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
                <Send className="w-10 h-10 text-emerald-600 -rotate-12" />
              </div>
              <div className="p-4">
                <p className="font-semibold text-gray-900 text-sm">Review Your Plan</p>
                <p className="text-xs text-gray-400 mt-0.5">Send to vendors in one tap</p>
                <div className="mt-3 space-y-1.5 text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Apr 13, 2026
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Los Angeles, CA
                  </div>
                </div>
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-medium">
                    Send My Event Plan <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value proposition */}
        <section className="pb-10" data-testid="landing-value-prop">
          <h2 className="text-xl font-bold text-gray-900 leading-snug">
            No searching. No back and forth.<br />
            Just real vendors ready to quote.
          </h2>
          <div className="mt-5 space-y-3">
            {[
              'Tell us your event details',
              'Send one request',
              'Get multiple quotes back'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* How it works — text steps */}
        <section className="pb-10" data-testid="landing-steps">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-6">Three Simple Steps</h2>
          <div className="space-y-4">
            {[
              { num: '1', title: 'Choose your event', desc: 'Birthday, wedding, corporate — you name it' },
              { num: '2', title: 'Select what you need', desc: 'DJ, photographer, catering, and more' },
              { num: '3', title: 'Get matched with vendors', desc: 'Real local vendors ready to quote your event' },
            ].map(step => (
              <div key={step.num} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{step.num}</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{step.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Secondary CTA */}
        <section className="pb-10 text-center" data-testid="landing-secondary-cta">
          <Button
            variant="outline"
            onClick={() => navigate('/plan-event')}
            className="rounded-full px-8 py-5 border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
            data-testid="landing-start-planning-btn"
          >
            Start planning your event <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          {/* Phase 4 — Concierge fork. Renders only when ENABLE_CONCIERGE_FLOW
              is on (resolved at runtime via /api/concierge/availability).
              When off, this block returns null and the landing experience is
              byte-for-byte identical to the legacy behaviour. */}
          {conciergeEnabled && (
            <div className="mt-4" data-testid="landing-concierge-fork">
              <button
                type="button"
                onClick={() => navigate('/concierge/intake')}
                className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 underline-offset-4 hover:underline"
                data-testid="landing-concierge-cta"
              >
                <Sparkles className="w-4 h-4" />
                Or — let Celey plan it for you
              </button>
            </div>
          )}
        </section>

        {/* iOS app promotion — hidden until REACT_APP_APP_STORE_URL is
            confirmed (see components/AppStorePromo.jsx hard gate). */}
        <AppStorePromo />
      </main>
      <Footer />
    </div>
  );
}
